import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from './landing/Navbar';
import Footer from './landing/Footer';
import { useLanguage } from '../context/LanguageContext';
import { isEnglishPath } from '../utils/i18nRoutes';

// Global Organization schema — rendered once, on every route (per SEO playbook §2.4)
const ORGANIZATION_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': 'https://innovation.valorizasm.com/#organization',
  name: 'Valoriza Servicios Medioambientales',
  alternateName: 'Valoriza Innovation Hub',
  url: 'https://innovation.valorizasm.com',
  logo: 'https://innovation.valorizasm.com/valoriza-logo.png',
  email: 'innovation@valorizasm.com',
  parentOrganization: {
    '@type': 'Organization',
    name: 'Sacyr',
    url: 'https://www.sacyr.com',
  },
  sameAs: [
    'https://www.valorizasm.com',
    'https://www.linkedin.com/company/valorizasm',
  ],
};

// Static tags in public/index.html exist as a fallback for crawlers that do not execute JS
// (e.g. some social share unfurlers). Once React mounts, react-helmet-async takes over and
// renders its own route-specific tags — so we remove the static fallbacks here to avoid
// duplicate/conflicting <meta>, <link> and <script type="application/ld+json"> tags.
const FALLBACK_TAG_IDS = [
  'fallback-description',
  'fallback-canonical',
  'fallback-og-type',
  'fallback-og-url',
  'fallback-og-site-name',
  'fallback-og-title',
  'fallback-og-description',
  'fallback-og-image',
  'fallback-og-image-width',
  'fallback-og-image-height',
  'fallback-og-image-alt',
  'fallback-og-locale',
  'fallback-twitter-card',
  'fallback-twitter-title',
  'fallback-twitter-description',
  'fallback-twitter-image',
  'fallback-jsonld-org',
  'fallback-jsonld-website',
];

export default function Layout() {
  const location = useLocation();
  const { setLang } = useLanguage();

  useEffect(() => {
    FALLBACK_TAG_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.remove();
    });
  }, []);

  // Real /en/* routes are the canonical English pages. Force the shared translations
  // (Home sections, ContactForm) to English there, and Spanish everywhere else — the
  // cosmetic language dropdown can still override this until the next navigation.
  useEffect(() => {
    setLang(isEnglishPath(location.pathname) ? 'en' : 'es');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div className="bg-light min-h-screen transition-colors duration-300">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ORGANIZATION_JSON_LD)}</script>
      </Helmet>

      {/* Film grain noise overlay */}
      <div className="noise-overlay" aria-hidden="true" />

      <Navbar />
      <main>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
